// Map visual (insight_map): plots rows with latitude/longitude columns onto an
// OpenStreetMap / Carto base map (Leaflet, lazy-loaded from CDN). It supports
// four point-based modes (the `mapMode` option), like mainstream BI map tiles:
//
//   • markers     — pins/circles, optionally graduated-coloured by a measure
//                   (the Colour group), with rich popups from the Top/Center/
//                   Bottom content groups.
//   • bubbles     — proportional-symbol map: circle radius ∝ a Size measure,
//                   colour ∝ the Colour measure.
//   • heatmap     — density heat layer (leaflet.heat) weighted by the Colour
//                   measure.
//   • microvisual — a small pie / donut / bars chart drawn at each location
//                   (Leaflet divIcon), split by the Category dimension and
//                   summed by the Value measure; rows at the same coordinate
//                   aggregate into one chart.
//   • choropleth  — shaded regions: bind a state/country column to the Region
//                   group and a measure to Colour, and each region is filled by
//                   its value. Boundaries come from /geo (see geoBoundaries.ts),
//                   downloaded per country on first use.
//
// Plus: keyless base maps (streets / light / dark), an optional colour legend,
// marker clustering (leaflet.markercluster) for dense data, and a travel-path
// polyline connecting points in row order (`showTravelPath`).
//
// Lat/lon bind from the Latitude/Longitude ColumnGroups first, falling back to
// a column-name regex so older saved models keep rendering.

import { useEffect, useRef, useState } from 'react'
import { ensureLeaflet, ensureLeafletHeat, ensureLeafletCluster } from './loader'
import { mapVisualData, pickGroup, toNumber, formatValue, type MappedData } from './dataUtils'
import { readStrOption, readNumOption, readBoolOption } from './optionUtils'
import {
  BoundariesUnavailable, detectRegions, loadBoundaries, loadGeoIndex, resolveFeatureId,
} from './geoBoundaries'
import type { NativeRendererProps } from './nativeRenderers'

// ── colour ramps (light → dark) used for graduated marker / heat colour ──────
const RAMPS: Record<string, string[]> = {
  blue: ['#e3f2fd', '#90caf9', '#42a5f5', '#1e88e5', '#0d47a1'],
  green: ['#e8f5e9', '#a5d6a7', '#66bb6a', '#43a047', '#1b5e20'],
  red: ['#ffebee', '#ef9a9a', '#ef5350', '#e53935', '#b71c1c'],
  orange: ['#fff3e0', '#ffcc80', '#ffa726', '#fb8c00', '#e65100'],
  purple: ['#f3e5f5', '#ce93d8', '#ab47bc', '#8e24aa', '#4a148c'],
  teal: ['#e0f2f1', '#80cbc4', '#26a69a', '#00897b', '#004d40'],
  viridis: ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725'],
  turbo: ['#30123b', '#28bceb', '#a2fc3c', '#fb8022', '#7a0403'],
}

// distinct categorical palette for micro-visual slices
const CAT_PALETTE = [
  '#1e88e5', '#43a047', '#e53935', '#fb8c00', '#8e24aa',
  '#00897b', '#fdd835', '#6d4c41', '#546e7a', '#d81b60',
]

function ramp(name: string): string[] {
  return RAMPS[name] ?? RAMPS.blue
}

/** Bucket a value into a ramp colour. Falls back to the ramp midpoint. */
function rampColor(name: string, v: number, min: number, max: number): string {
  const r = ramp(name)
  if (!Number.isFinite(v) || max <= min) return r[Math.floor(r.length / 2)]
  const t = (v - min) / (max - min)
  const idx = Math.min(r.length - 1, Math.max(0, Math.floor(t * r.length)))
  return r[idx]
}

/** leaflet.heat gradient stops built from the chosen ramp. */
function heatGradient(name: string): Record<number, string> {
  const r = ramp(name)
  const out: Record<number, string> = {}
  r.forEach((c, i) => { out[Number(((i + 1) / r.length).toFixed(2))] = c })
  return out
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string)
}

// ── base-map tile definitions (all keyless) ──────────────────────────────────
// CARTO's basemaps now stamp "API KEY REQUIRED" over every keyless tile, so
// light/dark use Esri's grey canvases (keyless; note the {z}/{y}/{x} order).
const OSM_ATTR = '© OpenStreetMap contributors'
const ESRI_ATTR = 'Tiles © Esri — Esri, HERE, Garmin, © OpenStreetMap contributors'
const ESRI_CANVAS = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas'
const BASEMAPS: Record<string, { url: string; attribution: string; subdomains?: string; maxZoom?: number }> = {
  streets: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: OSM_ATTR },
  light: { url: `${ESRI_CANVAS}/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`, attribution: ESRI_ATTR, maxZoom: 16 },
  dark: { url: `${ESRI_CANVAS}/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`, attribution: ESRI_ATTR, maxZoom: 16 },
}

// ── column binding helpers ───────────────────────────────────────────────────
function groupCol(md: MappedData, ...names: string[]): string | undefined {
  const g = pickGroup(md, ...names)
  return g.length ? g[0].name : undefined
}

const CONTENT_GROUPS = ['topleft', 'topright', 'centerfirstrow', 'centersecondrow', 'bottomleft', 'bottomright']

// ── micro-visual: aggregate a location's rows into coloured slices ───────────
interface Slice { label: string; value: number; color: string }

function buildSlices(
  rows: Array<Record<string, unknown>>,
  catName: string,
  valName: string | undefined,
  colorOf: (label: string) => string,
): Slice[] {
  const order: string[] = []
  const sums = new Map<string, number>()
  for (const r of rows) {
    const label = String(r[catName] ?? '')
    const v = valName ? toNumber(r[valName]) : 1
    if (!sums.has(label)) { sums.set(label, 0); order.push(label) }
    sums.set(label, sums.get(label)! + v)
  }
  let slices = order.map((l) => ({ label: l, value: sums.get(l)!, color: colorOf(l) }))
  slices.sort((a, b) => b.value - a.value)
  return slices.filter((s) => s.value > 0)
}

/** SVG string for a pie / donut using the stroke-dasharray disk trick. */
function sliceSvg(slices: Slice[], size: number, donut: boolean): string {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1
  const sw = donut ? size * 0.3 : size / 2
  const r = donut ? (size - sw) / 2 : size / 4
  const c = 2 * Math.PI * r
  const cx = size / 2
  let offset = 0
  const rings = slices.map((s) => {
    const dash = (s.value / total) * c
    const el =
      `<circle cx="${cx}" cy="${cx}" r="${r}" fill="none" stroke="${s.color}" ` +
      `stroke-width="${sw}" stroke-dasharray="${dash} ${c - dash}" ` +
      `stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cx})"/>`
    offset += dash
    return el
  }).join('')
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<circle cx="${cx}" cy="${cx}" r="${size / 2 - 1}" fill="rgba(255,255,255,.85)" stroke="#0006" stroke-width="1"/>` +
    rings +
    (donut ? '' : `<circle cx="${cx}" cy="${cx}" r="${size / 2 - 1}" fill="none" stroke="#fff" stroke-width="1"/>`) +
    `</svg>`
}

/** SVG string for a mini vertical bar chart. */
function barsSvg(slices: Slice[], size: number): string {
  const max = Math.max(...slices.map((s) => s.value), 0) || 1
  const pad = 3
  const n = slices.length || 1
  const slot = (size - pad * 2) / n
  const bw = Math.max(2, slot * 0.7)
  const bars = slices.map((s, i) => {
    const h = Math.max(1, (s.value / max) * (size - pad * 2))
    const x = pad + i * slot + (slot - bw) / 2
    const y = size - pad - h
    return `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${h.toFixed(1)}" rx="0.6" fill="${s.color}"/>`
  }).join('')
  return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
    `<rect x="0" y="0" width="${size}" height="${size}" rx="4" fill="rgba(255,255,255,.85)" stroke="#0006" stroke-width="1"/>` +
    bars + `</svg>`
}

// ── rows the map could not place ─────────────────────────────────────────────

/** One value the map could not place: a region name with no shape, or rows
 *  with no coordinates. `raw` is what the column holds (for selection). */
interface Unplaced { label: string; raw: unknown; value: number; rows: number }

const UNPLACED_SHOWN = 6

// Quarter-step zoom: fitting the data to the tile with whole steps drops a full
// level whenever the region is slightly too tall (India in a short tile).
const MAP_ZOOM_SNAP = 0.25

/**
 * A map must never silently drop data: rows it could not place are always
 * listed in a corner box with their totals — independent of the legend option,
 * because hiding them makes the shaded map look like the whole picture.
 * Clicking a value selects it, like clicking a region.
 */
function addUnplacedControl(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Lf: any, map: any, items: Unplaced[], measureLabel: string,
  onPick: ((item: Unplaced) => void) | null,
): void {
  if (!items.length) return
  const sorted = [...items].sort((x, y) => y.value - x.value)
  const total = sorted.reduce((s, x) => s + x.value, 0)
  const ctl = Lf.control({ position: 'bottomleft' })
  ctl.onAdd = () => {
    const div = Lf.DomUtil.create('div', 'viz-map-unplaced')
    div.setAttribute('data-tw-map-unplaced', String(sorted.length))
    div.style.cssText = 'background:rgba(255,255,255,.92);padding:6px 8px;border-radius:6px;font-size:11px;line-height:1.5;color:#222;box-shadow:0 1px 4px rgba(0,0,0,.2);max-width:240px'
    const line = (it: Unplaced, i: number) =>
      `<div data-i="${i}" style="display:flex;gap:8px;justify-content:space-between;${onPick ? 'cursor:pointer' : ''}">` +
      `<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${esc(it.label)}</span>` +
      `<b>${esc(formatValue(it.value, 'number'))}</b></div>`
    const more = sorted.length - UNPLACED_SHOWN
    div.innerHTML =
      `<div style="margin-bottom:2px"><b>Not on map</b> <span style="opacity:.65">— ${esc(measureLabel)} ${esc(formatValue(total, 'number'))}</span></div>` +
      sorted.slice(0, UNPLACED_SHOWN).map(line).join('') +
      (more > 0 ? `<div style="opacity:.6">+${more} more</div>` : '')
    Lf.DomEvent.disableClickPropagation(div)
    if (onPick) {
      div.addEventListener('click', (ev: MouseEvent) => {
        const el = (ev.target as HTMLElement)?.closest?.('[data-i]') as HTMLElement | null
        const it = el ? sorted[Number(el.dataset.i)] : undefined
        if (it) onPick(it)
      })
    }
    return div
  }
  ctl.addTo(map)
}

// ── choropleth: shade regions by a measure ───────────────────────────────────

interface ChoroplethArgs {
  container: HTMLDivElement | null
  md: MappedData
  keys: string[]
  basemap: string
  scheme: string
  showLegend: boolean
  geoLevel: string
  geoCountry: string
  isCancelled: () => boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setMap: (m: any) => void
  setErr: (m: string | null) => void
  setNotice: (m: string | null) => void
  /** Row-selection handler, read at click time so it is never a stale closure. */
  emit: (row: Record<string, unknown>) => void
}

/** Column holding the region name/code: the Region group, else a column whose
 *  NAME reads like one, else the first non-numeric column. */
function pickRegionKey(md: MappedData, keys: string[]): string | undefined {
  const bound = groupCol(md, 'region')
  if (bound) return bound
  const nonNumeric = keys.filter((k) => k !== 'id' && md.schema[k]?.type !== 'number')
  return nonNumeric.find((k) => /state|province|region|country|territory|district|county/i.test(k))
    ?? nonNumeric[0]
}

/** Measure that colours the regions: the Colour group, else Value/Size, else
 *  the first numeric column. */
function pickMeasureKey(md: MappedData, keys: string[]): string | undefined {
  return groupCol(md, 'color') ?? groupCol(md, 'value') ?? groupCol(md, 'size')
    ?? keys.find((k) => k !== 'id' && md.schema[k]?.type === 'number')
}

async function renderChoropleth(a: ChoroplethArgs): Promise<void> {
  const { md, keys, isCancelled, setErr, setNotice } = a
  const regionKey = pickRegionKey(md, keys)
  const measureKey = pickMeasureKey(md, keys)
  if (!regionKey) {
    setErr('A choropleth needs a region column — map the state / country column to the Region group.')
    return
  }
  if (!measureKey) {
    setErr('A choropleth needs a numeric measure — map one to the Colour group.')
    return
  }

  const index = await loadGeoIndex()
  if (isCancelled()) return
  const detection = detectRegions(
    md.rows.map((r) => r[regionKey]), index, a.geoLevel, a.geoCountry,
  )

  // Sum the measure per region (several rows can share one region).
  const totals = new Map<string, number>()
  // …and remember the region value AS THE DATA SPELLS IT, so a click filters on
  // what the column actually contains ("NY") rather than on the geo feature id
  // the boundary file uses. First spelling wins: if two spellings resolve to one
  // region they were merged into one shape, and only one can be filtered on.
  const regionRaw = new Map<string, unknown>()
  const unmatched: string[] = []
  const unplaced = new Map<string, Unplaced>()
  for (const r of md.rows) {
    const raw = r[regionKey]
    const fid = resolveFeatureId(raw, index, detection)
    if (!fid) {
      const label = String(raw ?? '').trim()
      if (label && !unmatched.includes(label) && unmatched.length < 5) unmatched.push(label)
      const key = label || '(blank)'
      const it = unplaced.get(key) ?? { label: key, raw, value: 0, rows: 0 }
      it.value += toNumber(r[measureKey])
      it.rows += 1
      unplaced.set(key, it)
      continue
    }
    totals.set(fid, (totals.get(fid) ?? 0) + toNumber(r[measureKey]))
    if (!regionRaw.has(fid)) regionRaw.set(fid, raw)
  }

  let fc
  try {
    fc = await loadBoundaries(detection, (msg) => { if (!isCancelled()) setNotice(msg) })
  } catch (e) {
    if (isCancelled()) return
    const err = e as BoundariesUnavailable
    setNotice(null)
    setErr(err.message)
    return
  }
  if (isCancelled()) return
  setNotice(null)

  if (!totals.size) {
    setErr(
      `None of the ${regionKey} values match ${detection.level === 'country' ? 'a country' : `a region of ${detection.country ?? 'that country'}`}` +
      (unmatched.length ? ` — e.g. ${unmatched.slice(0, 3).map((u) => `“${u}”`).join(', ')}` : '') +
      '. Use full names or ISO codes, or set the visual’s Country option.',
    )
    return
  }

  const values = [...totals.values()].filter(Number.isFinite)
  const vMin = Math.min(...values)
  const vMax = Math.max(...values)
  const measureLabel = md.groups['color']?.[0]?.label ?? md.groups['value']?.[0]?.label ?? measureKey

  const L = await ensureLeaflet()
  if (isCancelled() || !a.container) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Lf = L as any
  const map = Lf.map(a.container, { scrollWheelZoom: false, zoomSnap: MAP_ZOOM_SNAP })
  a.setMap(map)
  const bm = BASEMAPS[a.basemap] ?? BASEMAPS.light
  Lf.tileLayer(bm.url, {
    attribution: bm.attribution, maxZoom: bm.maxZoom ?? 19,
    ...(bm.subdomains ? { subdomains: bm.subdomains } : {}),
  }).addTo(map)

  const valueOf = (feature: { properties?: Record<string, unknown> }): number | undefined => {
    const id = String(feature?.properties?.id ?? '')
    return totals.has(id) ? totals.get(id) : undefined
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const styleFor = (feature: any) => {
    const v = valueOf(feature)
    if (v === undefined) {
      // A region the query returned no row for — kept visible but muted, so
      // the map reads as "no data here", not "this place doesn't exist".
      return { fillColor: '#cfd8dc', fillOpacity: 0.35, color: '#ffffff', weight: 1, dashArray: '2 3' }
    }
    return {
      fillColor: rampColor(a.scheme, v, vMin, vMax), fillOpacity: 0.85,
      color: '#ffffff', weight: 1,
    }
  }

  // Frame the regions that actually carry data. Fitting the whole layer would
  // show the entire planet for "revenue in France and Spain".
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dataBounds: any = null
  const layer = Lf.geoJSON(fc, {
    style: styleFor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onEachFeature: (feature: any, lyr: any) => {
      const name = String(feature?.properties?.name ?? feature?.properties?.id ?? '')
      const v = valueOf(feature)
      if (v !== undefined && lyr.getBounds) {
        const b = lyr.getBounds()
        if (b?.isValid?.()) dataBounds = dataBounds ? dataBounds.extend(b) : b
      }
      lyr.bindTooltip(
        `<div style="font-size:12px"><b>${esc(name)}</b><br/>` +
        `<span style="opacity:.7">${esc(measureLabel)}:</span> ` +
        `${v === undefined ? '—' : esc(formatValue(v, 'number'))}</div>`,
        { sticky: true },
      )
      lyr.on('mouseover', () => { lyr.setStyle({ weight: 2, color: '#37474f' }); lyr.bringToFront?.() })
      lyr.on('mouseout', () => { lyr.setStyle(styleFor(feature)) })
      // Clicking a region selects it — the same contract every other visual
      // honours, so drill-through and the dashboard quick filter work on a map.
      // A region the query returned no row for carries no value to filter by.
      lyr.on('click', () => {
        const fid = String(feature?.properties?.id ?? '')
        if (!regionRaw.has(fid)) return
        a.emit({ [regionKey]: regionRaw.get(fid), [measureKey]: totals.get(fid) })
      })
    },
  })
  layer.addTo(map)

  if (a.showLegend && vMax > vMin) {
    const legend = Lf.control({ position: 'bottomright' })
    legend.onAdd = () => {
      const div = Lf.DomUtil.create('div', 'viz-map-legend')
      div.style.cssText = 'background:rgba(255,255,255,.9);padding:6px 8px;border-radius:6px;font-size:11px;line-height:1.5;color:#222;box-shadow:0 1px 4px rgba(0,0,0,.2)'
      const stops = ramp(a.scheme).map((c) => `<span style="flex:1;background:${c};height:8px"></span>`).join('')
      div.innerHTML =
        `<div style="margin-bottom:3px">${esc(measureLabel)}</div>` +
        `<div style="display:flex;width:110px">${stops}</div>` +
        `<div style="display:flex;justify-content:space-between"><span>${formatValue(vMin, 'number')}</span><span>${formatValue(vMax, 'number')}</span></div>`
      return div
    }
    legend.addTo(map)
  }
  addUnplacedControl(Lf, map, [...unplaced.values()], measureLabel,
    (it) => a.emit({ [regionKey]: it.raw, [measureKey]: it.value }))

  try {
    const bounds = dataBounds ?? layer.getBounds()
    if (bounds?.isValid?.()) map.fitBounds(bounds, { padding: [20, 20] })
    else map.setView([20, 0], 2)
  } catch {
    map.setView([20, 0], 2)
  }
  requestAnimationFrame(() => { if (!isCancelled()) map.invalidateSize() })
}

export function MapVisual({ jsonData, visualLayout, optionsArr, onSelect }: NativeRendererProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [err, setErr] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // Held in a ref, not read from the closure: the handler's identity changes on
  // every parent render (it is rebuilt when quick-filter mode toggles), and
  // putting it in the effect's deps would tear down and rebuild the whole
  // Leaflet map — losing the user's pan/zoom — every time.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let map: any = null
    let raf = 0
    setErr(null)
    setNotice(null)

    const options = optionsArr ?? visualLayout?.options
    const mode = readStrOption(options, 'mapMode', 'markers').toLowerCase()
    const basemap = readStrOption(options, 'basemap', 'light').toLowerCase()
    const scheme = readStrOption(options, 'colorScheme', 'blue').toLowerCase()
    const showLegend = readBoolOption(options, 'legend', true)
    const cluster = readBoolOption(options, 'clusterMarkers', false)
    const heatRadius = readNumOption(options, 'heatRadius', 25)
    const heatBlur = readNumOption(options, 'heatBlur', 15)
    const microKind = readStrOption(options, 'microVisual', 'pie').toLowerCase()
    const microSize = readNumOption(options, 'microSize', 46)
    const travelPath = /^(on|true|1)$/i.test(readStrOption(options, 'showTravelPath', 'off'))

    const md = mapVisualData(jsonData, visualLayout)
    const keys = Object.keys(md.schema)

    if (mode === 'choropleth') {
      const geoLevel = readStrOption(options, 'geoLevel', 'auto')
      const geoCountry = readStrOption(options, 'geoCountry', '')
      renderChoropleth({
        container: ref.current, md, keys, basemap, scheme, showLegend,
        geoLevel, geoCountry,
        isCancelled: () => cancelled,
        setMap: (m) => { map = m },
        setErr, setNotice,
        emit: (row) => { onSelectRef.current?.(row) },
      }).catch((e) => { if (!cancelled) setErr(String(e?.message ?? e)) })
      return () => {
        cancelled = true
        if (raf) cancelAnimationFrame(raf)
        if (map) map.remove()
      }
    }

    const latKey = groupCol(md, 'latitude') ?? keys.find((k) => /^lat|latitude/i.test(k))
    const lonKey = groupCol(md, 'longitude') ?? keys.find((k) => /^(lon|lng)|longitude/i.test(k))
    if (!latKey || !lonKey) {
      setErr('Map needs latitude and longitude columns (map them to the Latitude / Longitude groups).')
      return
    }

    const colorKey = groupCol(md, 'color')
    const sizeKey = groupCol(md, 'size') ?? colorKey
    const catKey = groupCol(md, 'category') ?? keys.find((k) => k !== latKey && k !== lonKey && md.schema[k]?.type !== 'number')
    const valueKey = groupCol(md, 'value') ?? colorKey ?? keys.find((k) => md.schema[k]?.type === 'number')
    // Popup label fallback when no content groups are mapped.
    const labelKey = keys.find((k) => k !== latKey && k !== lonKey && md.schema[k]?.type !== 'number')

    // Colour scale over the Colour measure.
    const colorNums = colorKey ? md.rows.map((r) => Number(r[colorKey])).filter(Number.isFinite) : []
    const cMin = colorNums.length ? Math.min(...colorNums) : 0
    const cMax = colorNums.length ? Math.max(...colorNums) : 0
    const accent = ramp(scheme)[3]
    const colorFor = (r: Record<string, unknown>): string =>
      colorKey ? rampColor(scheme, Number(r[colorKey]), cMin, cMax) : accent

    // Bubble radius scale over the Size measure (sqrt so area reads linearly).
    const sizeNums = sizeKey ? md.rows.map((r) => Number(r[sizeKey])).filter(Number.isFinite) : []
    const sMin = sizeNums.length ? Math.min(...sizeNums) : 0
    const sMax = sizeNums.length ? Math.max(...sizeNums) : 0
    const R_MIN = 6, R_MAX = 30
    const radiusFor = (r: Record<string, unknown>): number => {
      if (!sizeKey || sMax <= sMin) return 10
      const v = Number(r[sizeKey])
      if (!Number.isFinite(v)) return R_MIN
      const t = Math.sqrt((v - sMin) / (sMax - sMin))
      return R_MIN + t * (R_MAX - R_MIN)
    }

    // Global category → colour map (stable colours across every location).
    const catColors = new Map<string, string>()
    if (catKey) {
      let i = 0
      for (const r of md.rows) {
        const label = String(r[catKey] ?? '')
        if (!catColors.has(label)) { catColors.set(label, CAT_PALETTE[i % CAT_PALETTE.length]); i++ }
      }
    }
    const catColorOf = (label: string): string => catColors.get(label) ?? CAT_PALETTE[0]

    const buildPopup = (r: Record<string, unknown>): string | null => {
      const parts: string[] = []
      for (const g of CONTENT_GROUPS) {
        for (const col of md.groups[g] ?? []) {
          if (col.hidden) continue
          const val = formatValue(r[col.name], md.schema[col.name]?.type)
          if (val !== '') parts.push(`<div><span style="opacity:.6">${esc(col.label)}:</span> ${esc(val)}</div>`)
        }
      }
      if (!parts.length && labelKey) {
        const val = formatValue(r[labelKey], md.schema[labelKey]?.type)
        if (val !== '') parts.push(`<div>${esc(val)}</div>`)
      }
      return parts.length ? `<div style="font-size:12px;line-height:1.5;min-width:80px">${parts.join('')}</div>` : null
    }

    ensureLeaflet()
      .then(async (L) => {
        if (cancelled || !ref.current) return
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Lf = L as any
        // scrollWheelZoom:false → wheel/two-finger scroll pages instead of
        // zooming; pinch (touchZoom) + the +/- control still zoom.
        map = Lf.map(ref.current, { scrollWheelZoom: false, zoomSnap: MAP_ZOOM_SNAP })
        const bm = BASEMAPS[basemap] ?? BASEMAPS.light
        Lf.tileLayer(bm.url, {
          attribution: bm.attribution, maxZoom: bm.maxZoom ?? 19,
          ...(bm.subdomains ? { subdomains: bm.subdomains } : {}),
        }).addTo(map)

        // Collect valid points.
        const pts: Array<{ lat: number; lon: number; r: Record<string, unknown> }> = []
        // Rows with no usable coordinates, grouped by their label — listed in
        // the "Not on map" box instead of vanishing.
        const noCoords = new Map<string, Unplaced>()
        for (const r of md.rows) {
          const lat = Number(r[latKey])
          const lon = Number(r[lonKey])
          const blank = (v: unknown) => v === null || v === undefined || String(v).trim() === ''
          if (!blank(r[latKey]) && !blank(r[lonKey]) && Number.isFinite(lat) && Number.isFinite(lon)) {
            pts.push({ lat, lon, r })
            continue
          }
          const raw = labelKey ? r[labelKey] : undefined
          const key = String(raw ?? '').trim() || '(no location)'
          const it = noCoords.get(key) ?? { label: key, raw, value: 0, rows: 0 }
          it.value += 1
          it.rows += 1
          noCoords.set(key, it)
        }
        addUnplacedControl(Lf, map, [...noCoords.values()], 'Rows',
          labelKey ? (it) => { onSelectRef.current?.({ [labelKey]: it.raw }) } : null)
        if (!pts.length) { map.setView([20, 0], 2); return }
        const latlngs = pts.map((p) => [p.lat, p.lon] as [number, number])

        if (mode === 'heatmap') {
          await ensureLeafletHeat()
          if (cancelled) return
          const wMax = cMax > cMin ? cMax : 1
          const heatPts = pts.map((p) => {
            const w = colorKey ? Number(p.r[colorKey]) : 1
            const weight = Number.isFinite(w) && wMax ? Math.max(0.1, w / wMax) : 0.5
            return [p.lat, p.lon, weight] as [number, number, number]
          })
          Lf.heatLayer(heatPts, {
            radius: heatRadius, blur: heatBlur, maxZoom: 17,
            gradient: heatGradient(scheme),
          }).addTo(map)
        } else {
          // marker / bubble / microvisual — optionally clustered.
          let layer = Lf.layerGroup()
          if (cluster && Lf.markerClusterGroup) {
            layer = Lf.markerClusterGroup({ showCoverageOnHover: false })
          } else if (cluster) {
            await ensureLeafletCluster()
            if (cancelled) return
            if (Lf.markerClusterGroup) layer = Lf.markerClusterGroup({ showCoverageOnHover: false })
          }

          if (mode === 'microvisual' && catKey) {
            // Group rows by coordinate, one overlay chart per location.
            const byLoc = new Map<string, Array<Record<string, unknown>>>()
            for (const p of pts) {
              const k = `${p.lat.toFixed(5)},${p.lon.toFixed(5)}`
              if (!byLoc.has(k)) byLoc.set(k, [])
              byLoc.get(k)!.push(p.r)
            }
            for (const [k, rows] of byLoc) {
              const [lat, lon] = k.split(',').map(Number)
              const slices = buildSlices(rows, catKey, valueKey, catColorOf)
              if (!slices.length) continue
              const svg = microKind === 'bars'
                ? barsSvg(slices, microSize)
                : sliceSvg(slices, microSize, microKind === 'donut')
              const icon = Lf.divIcon({
                html: svg, className: 'viz-map-micro',
                iconSize: [microSize, microSize], iconAnchor: [microSize / 2, microSize / 2],
              })
              const marker = Lf.marker([lat, lon], { icon })
              // One overlay chart covers every row at this location; selecting
              // it means "this place", so emit the first row — its location
              // dimensions are what a filter can act on, and the per-category
              // split is what the popup is for.
              marker.on('click', () => { onSelectRef.current?.(rows[0]) })
              const total = slices.reduce((s, x) => s + x.value, 0)
              const rows2 = slices.map((s) =>
                `<div><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${s.color};margin-right:4px"></span>` +
                `${esc(s.label)}: ${formatValue(s.value, 'number')} (${total ? Math.round((s.value / total) * 100) : 0}%)</div>`).join('')
              marker.bindPopup(`<div style="font-size:12px;line-height:1.5">${rows2}</div>`)
              layer.addLayer ? layer.addLayer(marker) : marker.addTo(layer)
            }
          } else {
            for (const p of pts) {
              const marker = Lf.circleMarker([p.lat, p.lon], {
                radius: mode === 'bubbles' ? radiusFor(p.r) : 7,
                fillColor: colorFor(p.r), color: '#ffffff', weight: 1, fillOpacity: 0.85,
              })
              // Clicking a marker selects its row, like clicking a bar or slice.
              marker.on('click', () => { onSelectRef.current?.(p.r) })
              const popup = buildPopup(p.r)
              if (popup) marker.bindPopup(popup)
              layer.addLayer ? layer.addLayer(marker) : marker.addTo(layer)
            }
          }
          layer.addTo(map)
        }

        // Travel path — connect points in row order.
        if (travelPath && latlngs.length > 1) {
          Lf.polyline(latlngs, { color: accent, weight: 2, opacity: 0.7, dashArray: '4 4' }).addTo(map)
        }

        // Legend — categorical swatches (micro-visual) or a graduated colour bar.
        const showCatLegend = mode === 'microvisual' && catColors.size > 0
        const showColorLegend = mode !== 'microvisual' && !!colorKey && cMax > cMin
        if (showLegend && (showCatLegend || showColorLegend)) {
          const legend = Lf.control({ position: 'bottomright' })
          legend.onAdd = () => {
            const div = Lf.DomUtil.create('div', 'viz-map-legend')
            div.style.cssText = 'background:rgba(255,255,255,.9);padding:6px 8px;border-radius:6px;font-size:11px;line-height:1.5;color:#222;box-shadow:0 1px 4px rgba(0,0,0,.2)'
            if (showCatLegend) {
              div.innerHTML = [...catColors].slice(0, 10).map(([label, color]) =>
                `<div><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};margin-right:5px"></span>${esc(label)}</div>`).join('')
            } else {
              const stops = ramp(scheme).map((c) => `<span style="flex:1;background:${c};height:8px"></span>`).join('')
              div.innerHTML =
                `<div style="margin-bottom:3px">${esc(md.groups['color']?.[0]?.label ?? colorKey)}</div>` +
                `<div style="display:flex;width:110px">${stops}</div>` +
                `<div style="display:flex;justify-content:space-between"><span>${formatValue(cMin, 'number')}</span><span>${formatValue(cMax, 'number')}</span></div>`
            }
            return div
          }
          legend.addTo(map)
        }

        map.fitBounds(latlngs, { padding: [30, 30] })
        raf = requestAnimationFrame(() => { if (!cancelled && map) map.invalidateSize() })
      })
      .catch((e) => { if (!cancelled) setErr(String(e?.message ?? e)) })

    return () => {
      cancelled = true
      if (raf) cancelAnimationFrame(raf)
      if (map) map.remove()
    }
  }, [jsonData, visualLayout, optionsArr])

  if (err) return <div style={{ padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center' }}>{err}</div>
  return (
    // `isolation` keeps Leaflet's z-indexes INSIDE the tile. Leaflet stacks its
    // panes at 200–700 and its controls at 800–1000, and `.leaflet-container` is
    // only `position:relative` — so without a stacking context here those numbers
    // compete with the host's chrome and the map paints over a dashboard's sticky
    // criteria bar (z-index 20) or a modal header. Isolated, the whole map stacks
    // as one element at the tile's own level.
    <div style={{ position: 'relative', isolation: 'isolate', width: '100%', height: '100%', minHeight: 280 }}>
      <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 280 }} />
      {notice && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16, fontSize: 13, color: 'var(--pg-muted, #888)', textAlign: 'center',
          background: 'var(--pg-surface, rgba(255,255,255,.6))',
        }}>{notice}</div>
      )}
    </div>
  )
}

export default MapVisual
